import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Redux
import { useAppDispatch } from 'hooks/useStore';
import { GroupsAsync } from 'store/group/groupAsync';
import MediaFormats from 'types/MediaFormats';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Input, Toggle } from 'components/Controls';
import { Button } from 'components/Buttons';
import { ColorPicker } from 'components/ColorPicker';
import ImageUpload from 'components/ImageUpload';
import StyledMuiButton from 'components/StyledMuiButton';
// MUI
import { ButtonGroup } from '@material-ui/core';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  onClose: () => void;
}

const CreateGroupFormDialog:FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  
  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      name: '',
      logoImage: '',
      logoFormat: MediaFormats.Logo,
      logoImageBackgroundColor: 'rgba(255, 255, 255, 0)',
      backgroundImage: 'https://media.quizwizards.com/bg-main-2.jpg',
      callToActionImageUrl: '',
      callToActionLinkUrl: '',
      callToActionButtonText: '',
      prizes: [],
      publicLeaderboard: {
        enabled: false,
        wallpaperImage: '',
        primaryColor: '#f99014',
        textColor: '#fff'
      },
      theme: {
        primaryColor: '#000',
        primaryFontColor: '#fff',
        primaryButtonGradientStart: '#facc22',
        primaryButtonGradientEnd: '#f83600',
        primaryButtonFontColor: '#282728'
      },
      resultsPage: {
        placingGradientStart: '#be7eea',
        placingGradientEnd: '#687fd9',
        placingFontColor: '#242324',
        currentPlacingGradientStart: '#e3722d',
        currentPlacingGradientEnd: '#f4d549',
        currentPlacingFontColor: '#242324',
      }
    }
  });

  const onSubmit = handleSubmit((data:any) => {
    setIsLoading(true);

    dispatch(GroupsAsync.createGroup(data)).unwrap()
      .then(() => onClose())
      .finally(() => setIsLoading(false));
  });

  const logoFormat = watch('logoFormat');

  return (
    <Dialog size="md" open={open} onClose={onClose}>
      <form className="flex flex-col overflow-y-auto">
        <DialogTitle title={t('dialogs.quizGroup.title')} onClose={onClose} />
        <span className="block text-gray-400 text-sm font-light leading-5 mb-4 pt-3 pr-3 pl-3">{t('dialogs.quizGroup.hint1')}</span>
        <span className="block text-gray-400 text-sm font-light leading-5 mb-4 pr-3 pl-3">{t('dialogs.quizGroup.hint2')}</span>
        <DialogContent>
          <Controller
            control={control} name="name" rules={{ required: isRequired, validate: noWhitespace }}
            render={({ field: { value, onChange } }) => (
              <Input
                label={t('labels.name')} id="name" name="name" value={value}
                required={true}
                onChange={onChange}
                error={Boolean(errors.name)}
                errorText={errors.name ? errors.name.message : ''}
              />
            )}
          />
          <div className="mb-4">
            <div className="mb-4">
              <h2 className="text-white text-2xl leading-6 font-bold">{t('labels.images')}</h2>
            </div>
            <Controller
              control={control} name="logoFormat"
              render={({ field: { onChange, value } }) => (
                <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                  <StyledMuiButton
                    variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                    onClick={() => onChange(MediaFormats.Logo)}
                  >{t('labels.squareLogo')}</StyledMuiButton>
                  <StyledMuiButton
                    variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                    onClick={() => onChange(MediaFormats.RectLogo)}
                  >{t('labels.rectangularLogo')}</StyledMuiButton>
                </ButtonGroup>
              )}
            />
            <Controller
              control={control} name="logoImage" rules={{ required: isRequired }}
              render={({ field }) => (
                <ImageUpload
                  {...field}
                  key={logoFormat}
                  format={logoFormat}
                  label="labels.logoImage"
                  className="w-1/4"
                  required={true}
                  error={Boolean(errors.logoImage)}
                  errorText={errors.logoImage ? errors.logoImage.message : ''}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              control={control} name="theme.primaryColor" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <ColorPicker
                  label={t('labels.primaryColor')}
                  value={value}
                  required={true}
                  error={Boolean(errors.theme?.primaryColor)}
                  errorText={errors.theme?.primaryColor ? errors.theme.primaryColor.message : ''}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div className="flex items-center mb-4">
            <span className="block text-white text-base font-semibold mr-3">{t('pages.groupForm.enabledPublicLeaderboardLabel')}</span>
            <Controller
              control={control} name="publicLeaderboard.enabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="publicLeaderboardEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <span className="block text-gray-400 text-sm">{t('pages.groupForm.enabledPublicLeaderboardHint')}</span>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
          >{t('buttons.cancel')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            loading={isLoading}
            onClick={onSubmit}
          >{t('buttons.create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CreateGroupFormDialog;
